// This is the ApprovalsTab component, which will display the approvals and the already approved dels that the user has approved. These will be separated by different tabs that the user can switch between.

import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { shallow } from "zustand/shallow";
import {
  getDeepLinkedDelId,
  buildCanonicalHomePath,
  parseHomeRoute,
} from "../../../../services/RouteUtils";
import {
  startTask,
  getTaskStatus,
  downloadTaskResult,
} from "../../../../api/TasksApi";
import { useAppStore } from "../../../../store/appStore";
import { useUserState } from "../../../../context/user";
import { copyDel } from "../../../../api/DelApi";
import { getLocalDateString } from "../../../../utils/dateUtils";
import { useFilters } from "../../../../hooks/useFilters";
import { useFilterMenu } from "../../../../hooks/useFilterMenu";
import { filtersInitialState } from "../../../../store/shared/filters/filtersState";
// Components
import { DelApprovalTable, DelApprovedTable } from "../del_table/index.js";
import {
  ApproveButton,
  CopyDelButton,
  TransferDelToDifferentApproverButton,
  ViewDelButton,
  DownloadPdfButton,
  RefreshDelListsButton,
  FilterButton,
  UnapproveDelButton,
} from "../buttons/DelActions.js";
import { DelFilterMenu } from "../../../common/FilterMenu.js";
import TransferDelModal from "../modal/TransferDelModal.js";
import DelForm from "../../../delForm/DelForm.js";
import styles from "./ApprovalsTab.module.css";

// Approval tab buttons
const ApprovalTabButtons = ({ activeApprovalTab, setActiveApprovalTab }) => {
  return (
    <div className={styles.tabButtons}>
      <button
        className={`${styles.tabButton} ${
          activeApprovalTab === "approvals" ? styles.active : ""
        }`}
        onClick={() => setActiveApprovalTab("approvals")}
      >
        Approvals
      </button>
      <button
        className={`${styles.tabButton} ${
          activeApprovalTab === "approved" ? styles.active : ""
        }`}
        onClick={() => setActiveApprovalTab("approved")}
      >
        Approved
      </button>
    </div>
  );
};

// Approvals Tab Content
const ApprovalsTabContent = ({
  user,
  employees,
  allEmployees,
  allProjectInfo,
  allInactiveProjects,
}) => {
  // UI States
  // Loading State
  const { isActive } = useAppStore((s) => s.loading, shallow);
  // Loading UI actions
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  const navigate = useNavigate();

  // DEL List States
  const approvals = useAppStore((s) => s.delLists.approvals.items, shallow);
  const totalCount = useAppStore(
    (s) => s.delLists.approvals.totalCount,
    shallow
  );
  const currentPage = useAppStore(
    (s) => s.delLists.approvals.currentPage,
    shallow
  );
  const selectedApproval = useAppStore(
    (s) => s.delLists.approvals.selectedApproval,
    shallow
  );
  const pageSize = useAppStore((s) => s.delLists.approvals.pageSize, shallow);

  // DEL UI States
  const { showApprovalsDelForm, showTransferApprovalModal } = useAppStore(
    (s) => s.approvalsTabUI,
    shallow
  );
  const activeDel = useAppStore((s) => s.activeDelApprovalsAwaiting, shallow);
  const activeDelActions = useAppStore(
    (s) => s.activeDelApprovalsAwaitingActions,
    shallow
  );

  // App Store Actions
  // Workflow Actions
  const transferApproval = useAppStore(
    (s) => s.delWorkflowActions.transferDelApproval
  );

  // Specific DEL Data actions
  const fetchDel = useAppStore(
    (s) => s.activeDelApprovalsAwaitingActions.fetchDel
  );

  // Specific DEL UI Actions
  const setShowDelForm = useAppStore(
    (s) => s.approvalsTabUIActions.setShowApprovalsDelForm
  );
  const setShowTransferApprovalModal = useAppStore(
    (s) => s.approvalsTabUIActions.setShowTransferApprovalModal
  );

  // Handling Page UI actions
  const pageSizeChange = useAppStore(
    (s) => s.delListsActions.setApprovalsPageSize
  );
  const setApprovalsPage = useAppStore(
    (s) => s.delListsActions.setApprovalsPage
  );
  const setApprovedPage = useAppStore((s) => s.delListsActions.setApprovedPage);
  const selectApprovalRow = useAppStore(
    (s) => s.delListsActions.setSelectedApproval
  );
  // Refreshing DEL Lists, both Drafts and Submits.
  const refreshApprovalsDelLists = useAppStore(
    (s) => s.delListsActions.fetchApprovalsPage
  );
  const refreshApprovedDelLists = useAppStore(
    (s) => s.delListsActions.fetchApprovedPage
  );

  // Local filter state (resets on unmount)
  const { filters: approvalsFilters, updateFilter: updateFilterAction, clearFilters: clearFiltersAction } = useFilters({ status: "1" });
  // Filter menu UI state
  const { showFilterMenu, toggleFilterMenu: handleToggleFilterMenu, filterPhaseList, setFilterPhaseList: filterPhaseActionFunction, filterMenuRef } = useFilterMenu();

  // Applying Filters
  const handleApplyFilters = async () => {
    showLoading("Loading DELs...");
    setApprovalsPage({ currentPage: 1, pageSize });
    await refreshApprovalsDelLists(user.employee_id, approvalsFilters);
    hideLoading();
  };
  // Clearing Filters
  const handleClearFilters = async () => {
    clearFiltersAction();
    setApprovalsPage({ currentPage: 1, pageSize });
    showLoading("Loading DELs...");
    await refreshApprovalsDelLists(user.employee_id, filtersInitialState({ status: "1" }));
    hideLoading();
  };

  // Specific DelForm Handlers
  const handleApproveDel = async () => {
    if (!selectedApproval) {
      window.alert("Please select a DEL to approve.");
      return;
    }
    const target = buildCanonicalHomePath({
      tab: "approvals",
      delId: selectedApproval.id,
      action: "approve",
      open: true,
    });
    navigate(target);
  };

  const handleDoubleClickApprove = async (del) => {
    if (!selectedApproval) {
      window.alert("Please select a DEL to approve.");
      return;
    }
    const target = buildCanonicalHomePath({
      tab: "approvals",
      delId: selectedApproval.id,
      action: "approve",
      open: true,
    });
    navigate(target);
  };

  const handleCopyDel = async () => {
    if (!selectedApproval) {
      window.alert("Please select a DEL to copy.");
      return;
    }
    try {
      showLoading("Copying DEL...");
      const resp = await copyDel(selectedApproval.id, user, "approvals");
      const { del_id, del_creation_date, summary } = resp || {};
      if (!del_id) throw new Error("Copy failed");
      if (summary) {
        useAppStore.getState().delListsActions.upsertDraftLocal(summary);
      } else {
        useAppStore.getState().delListsActions.upsertDraftLocal({
          id: del_id,
          del_creation_date: del_creation_date || getLocalDateString(),
          updated: getLocalDateString(),
          project_number: "-",
        });
      }
      useAppStore.getState().notificationActions.showNotification({
        type: "success",
        title: "DEL Copied",
        message: "Your DEL has been copied. You can edit it in the Drafts tab.",
      });
      hideLoading();
    } catch (error) {
      hideLoading();
      useAppStore.getState().notificationActions.showNotification({
        type: "error",
        title: "Error Copying DEL",
        message: "Please try again later.",
      });
      // eslint-disable-next-line no-console
      console.error("Error copying DEL:", error);
    }
  };

  // Handlers for transfer of DEL waiting for approval
  const handleShowTransferApprovalModal = () => {
    if (!selectedApproval) {
      window.alert("Please select a DEL to transfer.");
      return;
    } else {
      setShowTransferApprovalModal(true);
    }
  };

  const handleTransferApproval = async (selectedEmployeeId) => {
    try {
      await transferApproval(selectedApproval.id, selectedEmployeeId);
    } catch (error) {
      console.error("Error transferring DEL approval:", error);
    }
  };

  // Handle change in page size
  const handlePageSizeChange = async (e) => {
    showLoading("Loading approvals...");
    const newPageSize = parseInt(e.target.value, 10);
    pageSizeChange(newPageSize);
    setApprovalsPage({ currentPage: 1, pageSize: newPageSize });
    await refreshApprovalsDelLists(user.employee_id, approvalsFilters);
    hideLoading();
  };

  const handlePageChange = async (direction) => {
    showLoading("Loading approvals...");
    let newPage;
    if (direction === "next") {
      newPage = currentPage + 1;
    } else {
      newPage = currentPage - 1;
    }
    setApprovalsPage({ currentPage: newPage, pageSize });
    await refreshApprovalsDelLists(user.employee_id, approvalsFilters);
    hideLoading();
  };

  const handleSelectApprovalRow = (del) => {
    selectApprovalRow(del);
  };

  // Adding refresh handler
  const handleRefreshDelLists = async () => {
    showLoading("Refreshing approvals...");
    setApprovalsPage({ currentPage: 1, pageSize });
    const approvedPageSize = useAppStore.getState().delLists.approved.pageSize;
    setApprovedPage({ currentPage: 1, pageSize: approvedPageSize });
    await refreshApprovalsDelLists(user.employee_id, approvalsFilters);
    await refreshApprovedDelLists(user.employee_id);
    hideLoading();
  };

  return (
    <>
      {showApprovalsDelForm ? (
        <>
          <DelForm
            activeDelTabInfo={activeDel}
            actionFunctions={activeDelActions}
            setShowDelForm={() => setShowDelForm(false)}
            tableName="approvals"
            refreshDelListFunction={handleRefreshDelLists}
          />
        </>
      ) : (
        <>
          <div className={styles.tableActions}>
            <ApproveButton approveFunction={handleApproveDel} />
            <CopyDelButton copyFunction={handleCopyDel} />
            <TransferDelToDifferentApproverButton
              showTransferDelModal={() => handleShowTransferApprovalModal()}
            />
          </div>
          {showTransferApprovalModal && (
            <TransferDelModal
              onTransferFunction={handleTransferApproval}
              onCancel={() => setShowTransferApprovalModal(false)}
              employees={employees}
              afterTransfer={() => {
                setShowTransferApprovalModal(false);
              }}
            />
          )}
          <div className={styles.headerWithRefresh}>
            <h2>Approvals</h2>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              ref={filterMenuRef}
            >
              <FilterButton filterFunction={handleToggleFilterMenu} />
              {showFilterMenu && (
                <DelFilterMenu
                  delTab="approvals"
                  filters={approvalsFilters}
                  updateFilterAction={updateFilterAction}
                  onApplyFilters={handleApplyFilters}
                  onClearFilters={handleClearFilters}
                  allProjectInfo={allProjectInfo}
                  allEmployees={allEmployees}
                  allInactiveProjects={allInactiveProjects}
                  filterPhaseList={filterPhaseList}
                  filterPhaseActionFunction={filterPhaseActionFunction}
                />
              )}
              <RefreshDelListsButton
                refreshFunction={handleRefreshDelLists}
                isLoading={isActive}
              />
            </div>
          </div>
          <div className={styles.tableWrapper}>
            {approvals?.length > 0 ? (
              <DelApprovalTable
                dels={approvals.slice(0, pageSize)}
                onRowSelect={handleSelectApprovalRow}
                onDoubleClick={handleDoubleClickApprove}
                selectedDelId={selectedApproval?.id}
              />
            ) : (
              <p className={styles.noData}>No approvals to display</p>
            )}
          </div>
          <div className={styles.paginationControls}>
            <div className={styles.paginationLeftControls}>
              <button
                onClick={() => handlePageChange("prev")}
                disabled={currentPage === 1 || isActive}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange("next")}
                disabled={currentPage * pageSize >= totalCount || isActive}
              >
                Next
              </button>
              <div className={styles.pageSizeSelector}>
                <label htmlFor="approvalsPageSize">Rows:</label>
                <select
                  id="approvalsPageSize"
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
            <span>
              Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} -{" "}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
              approvals
            </span>
          </div>
        </>
      )}
    </>
  );
};

// Approved Tab Content
const ApprovedTabContent = ({
  user,
  employees,
  allEmployees,
  allProjectInfo,
  allInactiveProjects,
}) => {
  // Loading State
  const { isActive } = useAppStore((s) => s.loading, shallow);
  // Loading UI actions
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  // Notification UI actions
  const showNotification = useAppStore(
    (s) => s.notificationActions.showNotification
  );
  // Navigation
  const navigate = useNavigate();

  //  Approved DEL Lists
  const approved = useAppStore((s) => s.delLists.approved.items, shallow);
  const totalCount = useAppStore(
    (s) => s.delLists.approved.totalCount,
    shallow
  );
  const currentPage = useAppStore(
    (s) => s.delLists.approved.currentPage,
    shallow
  );
  const selectedApproved = useAppStore(
    (s) => s.delLists.approved.selectedApproved,
    shallow
  );
  const pageSize = useAppStore((s) => s.delLists.approved.pageSize, shallow);

  // DEL UI States
  const { showApprovedDelForm } = useAppStore((s) => s.approvalsTabUI, shallow);
  const activeDel = useAppStore((s) => s.activeDelApproved, shallow);
  const activeDelActions = useAppStore(
    (s) => s.activeDelApprovedActions,
    shallow
  );

  // Specific DEL data actions
  const fetchDel = useAppStore((s) => s.activeDelApprovedActions.fetchDel);
  // Unapprove DEL action
  const unapproveDel = useAppStore((s) => s.delWorkflowActions.unapproveDel);

  // Specific DEL UI actions
  const setShowDelForm = useAppStore(
    (s) => s.approvalsTabUIActions.setShowApprovedDelForm
  );
  const setActiveApprovalTab = useAppStore(
    (s) => s.approvalsTabUIActions.setActiveApprovalTab
  );

  // Handling Page UI actions
  const pageSizeChange = useAppStore(
    (s) => s.delListsActions.setApprovedPageSize
  );
  const setApprovedPage = useAppStore((s) => s.delListsActions.setApprovedPage);
  const setApprovalsPage = useAppStore(
    (s) => s.delListsActions.setApprovalsPage
  );
  const selectApprovedRow = useAppStore(
    (s) => s.delListsActions.setSelectedApproved
  );
  const toggleSelectedApproved = useAppStore(
    (s) => s.delListsActions.toggleSelectedApproved
  );
  const clearSelectedApprovedIds = useAppStore(
    (s) => s.delListsActions.clearSelectedApprovedIds
  );
  // Refreshing DEL lists, both approved and approvals
  const refreshApprovedDelLists = useAppStore(
    (s) => s.delListsActions.fetchApprovedPage
  );
  const refreshApprovalsDelLists = useAppStore(
    (s) => s.delListsActions.fetchApprovalsPage
  );

  const selectedApprovedIds = useAppStore(
    (s) => s.delLists.approved.selectedApprovedIds
  );
  const handleDownloadPdfs = async () => {
    if ((selectedApprovedIds || []).length === 0) {
      showNotification({
        type: "error",
        title: "No DELs selected",
        message: "Please select at least one DEL to download.",
      });
      return;
    }
    if (selectedApprovedIds.length > 50) {
      showNotification({
        type: "error",
        title: "Too many DELs selected",
        message:
          "You can only download up to 50 DELs at a time. Please reduce your selection.",
      });
      return;
    }
    showLoading("Downloading DELs...");
    try {
      const { data: start } = await startTask("del_ops.create_pdfs", {
        del_ids: selectedApprovedIds,
      });
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start PDF task");
      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      // 150 attempts x 2s = 5 minutes for bulk PDFs (up to 50 DELs)
      const maxAttempts = 150;
      while (!ready && attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready)
        throw new Error(
          "Timed out waiting for PDFs. The task may still be running."
        );
      if (failed) throw new Error(errorMessage || "Task failed");
      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/zip",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match ? decodeURIComponent(match[1]) : "DELs.zip";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "PDF Download Failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleDownloadPdf = async (approvedId) => {
    if (!approvedId) {
      showNotification({
        type: "error",
        title: "No DEL selected",
      });
      return;
    }
    showLoading("Downloading DEL...");
    try {
      const { data: start } = await startTask("del_ops.create_single_pdf", {
        del_id: approvedId,
      });
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start PDF task");
      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      while (!ready && attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready) throw new Error("Timed out waiting for PDF");
      if (failed) throw new Error(errorMessage || "Task failed");
      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/pdf",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match ? decodeURIComponent(match[1]) : "DEL.pdf";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "PDF Download Failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  // Local filter state (resets on unmount)
  const { filters: approvedFilters, updateFilter: updateFilterAction, clearFilters: clearFiltersAction } = useFilters({ status: "2" });
  // Filter menu UI state
  const { showFilterMenu, toggleFilterMenu: handleToggleFilterMenu, filterPhaseList, setFilterPhaseList: filterPhaseActionFunction, filterMenuRef } = useFilterMenu();

  const handleApplyFilters = async () => {
    showLoading("Loading DELs...");
    setApprovedPage({ currentPage: 1, pageSize });
    await refreshApprovedDelLists(user.employee_id, approvedFilters);
    hideLoading();
  };
  const handleClearFilters = async () => {
    clearFiltersAction();
    setApprovedPage({ currentPage: 1, pageSize });
    showLoading("Loading DELs...");
    await refreshApprovedDelLists(user.employee_id, filtersInitialState({ status: "2" }));
    hideLoading();
  };

  // Specific DelForm Handlers
  const handleViewDel = async () => {
    if (!selectedApproved) {
      window.alert("Please select a DEL to view.");
      return;
    }
    const target = buildCanonicalHomePath({
      tab: "approvals",
      delId: selectedApproved.id,
      action: "view",
      open: true,
    });
    navigate(target);
  };

  const handleDoubleClickView = async (del) => {
    if (!selectedApproved) {
      window.alert("Please select a DEL to view.");
      return;
    }
    const target = buildCanonicalHomePath({
      tab: "approvals",
      delId: selectedApproved.id,
      action: "view",
      open: true,
    });
    navigate(target);
  };

  const handleCopyDel = async () => {
    if (!selectedApproved) {
      window.alert("Please select a DEL to copy.");
      return;
    }
    try {
      showLoading("Copying DEL...");
      const resp = await copyDel(selectedApproved.id, user, "approved");
      const { del_id, del_creation_date, summary } = resp || {};
      if (!del_id) throw new Error("Copy failed");
      if (summary) {
        useAppStore.getState().delListsActions.upsertDraftLocal(summary);
      } else {
        useAppStore.getState().delListsActions.upsertDraftLocal({
          id: del_id,
          del_creation_date: del_creation_date || getLocalDateString(),
          updated: getLocalDateString(),
          project_number: "-",
        });
      }
      useAppStore.getState().notificationActions.showNotification({
        type: "success",
        title: "DEL Copied",
        message: "Your DEL has been copied. You can edit it in the Drafts tab.",
      });
      hideLoading();
    } catch (error) {
      hideLoading();
      useAppStore.getState().notificationActions.showNotification({
        type: "error",
        title: "Error Copying DEL",
        message: "Please try again later.",
      });
      // eslint-disable-next-line no-console
      console.error("Error copying DEL:", error);
    }
  };

  const handleUnapproveDel = async () => {
    if (!selectedApproved) {
      window.alert("Please select a DEL to unapprove.");
      return;
    }
    if (selectedApproved.canUnapprove !== true) {
      showNotification({
        type: "error",
        title: "Error Unapproving DEL",
        message:
          "This DEL is not eligible for unapproval, please contact the Deltek admin or DEL app support.",
      });
      return;
    }
    try {
      showLoading("Unapproving DEL...");
      const resp = await unapproveDel(selectedApproved.id, user);
      if (resp.ok) {
        handleRefreshDelLists();
        showNotification({
          type: "success",
          title: "DEL Unapproved",
          message: "Your DEL was unapproved successfully.",
        });
      } else {
        showNotification({
          type: "error",
          title: "Error Unapproving DEL",
          message: "Please try again later.",
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        title: "Error Unapproving DEL",
        message: "Please try again later.",
      });
      // eslint-disable-next-line no-console
      console.error("Error unapproving DEL:", error);
    } finally {
      hideLoading();
    }
  };

  // Handlers for pagination
  // Handle change in page size
  const handlePageSizeChange = async (e) => {
    showLoading("Loading DELs...");
    const newPageSize = parseInt(e.target.value, 10);
    pageSizeChange(newPageSize);
    await refreshApprovedDelLists(user.employee_id, approvedFilters);
    hideLoading();
  };

  const handlePageChange = async (direction) => {
    showLoading("Loading DELs...");
    let newPage;
    if (direction === "next") {
      newPage = currentPage + 1;
    } else {
      newPage = currentPage - 1;
    }
    setApprovedPage({ currentPage: newPage, pageSize });
    await refreshApprovedDelLists(user.employee_id, approvedFilters);
    hideLoading();
  };

  // Refresh handler
  const handleRefreshDelLists = async () => {
    showLoading("Refreshing DELs...");
    setApprovedPage({ currentPage: 1, pageSize });
    const approvalsPageSize = useAppStore.getState().delLists.approvals.pageSize;
    setApprovalsPage({ currentPage: 1, pageSize: approvalsPageSize });
    await refreshApprovedDelLists(user.employee_id, approvedFilters);
    await refreshApprovalsDelLists(user.employee_id);
    hideLoading();
  };

  // Monitor selection count and warn if > 50
  useEffect(() => {
    if (selectedApprovedIds.length > 50) {
      showNotification({
        type: "warning",
        title: "Selection limit exceeded",
        message: `You have selected ${selectedApprovedIds.length} DELs. You can only download 50 at a time. Please deselect some DELs before downloading.`,
      });
    }
  }, [selectedApprovedIds, showNotification]);

  return (
    <>
      {showApprovedDelForm ? (
        <>
          <DelForm
            activeDelTabInfo={activeDel}
            actionFunctions={activeDelActions}
            setShowDelForm={() => setShowDelForm(false)}
            tableName="approvals"
            copyDelFunction={handleCopyDel}
            refreshDelListFunction={handleRefreshDelLists}
            unapproveDelOption={true}
          />
        </>
      ) : (
        <>
          <div className={styles.tableActions}>
            <ViewDelButton viewFunction={handleViewDel} />
            <CopyDelButton copyFunction={handleCopyDel} />
            <UnapproveDelButton
              unapproveFunction={handleUnapproveDel}
              disabled={selectedApproved?.canUnapprove !== true}
            />
          </div>
          <div className={styles.headerWithRefresh}>
            <h2>Approved </h2>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              ref={filterMenuRef}
            >
              <DownloadPdfButton onClick={handleDownloadPdfs} />
              <FilterButton filterFunction={handleToggleFilterMenu} />
              {showFilterMenu && (
                <DelFilterMenu
                  delTab="approved"
                  filters={approvedFilters}
                  updateFilterAction={updateFilterAction}
                  onApplyFilters={handleApplyFilters}
                  onClearFilters={handleClearFilters}
                  allProjectInfo={allProjectInfo}
                  allEmployees={allEmployees}
                  allInactiveProjects={allInactiveProjects}
                  filterPhaseList={filterPhaseList}
                  filterPhaseActionFunction={filterPhaseActionFunction}
                />
              )}
              <RefreshDelListsButton
                refreshFunction={handleRefreshDelLists}
                isLoading={isActive}
              />
            </div>
          </div>
          <div className={styles.tableWrapper}>
            {approved.length > 0 ? (
              <DelApprovedTable
                dels={approved.slice(0, pageSize)}
                onRowSelect={selectApprovedRow}
                onDoubleClick={handleDoubleClickView}
                selectedDelId={selectedApproved?.id}
                selectedIds={selectedApprovedIds}
                onToggleSelect={toggleSelectedApproved}
                onDownloadSingle={handleDownloadPdf}
                clearSelectedApprovedIds={clearSelectedApprovedIds}
              />
            ) : (
              <p className={styles.noData}>No approved dels to display</p>
            )}
          </div>
          <div className={styles.paginationControls}>
            <div className={styles.paginationLeftControls}>
              <button
                onClick={() => handlePageChange("prev")}
                disabled={currentPage === 1 || isActive}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange("next")}
                disabled={currentPage * pageSize >= totalCount || isActive}
              >
                Next
              </button>
              <div className={styles.pageSizeSelector}>
                <label htmlFor="approvedPageSize">Rows:</label>
                <select
                  id="approvedPageSize"
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
            <span>
              Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} -{" "}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
              approved
            </span>
          </div>
        </>
      )}
    </>
  );
};

// Approvals Tab
export const ApprovalsTab = () => {
  // User Info
  const user = useUserState().currentUser;
  // Employees
  const employees = useAppStore((s) => s.employees.list, shallow);
  // Terminated employees
  const allTerminatedEmployees = useAppStore(
    (s) => s.employees.terminatedList,
    shallow
  );
  const allSearchEmployees = [...employees, ...allTerminatedEmployees];
  // Projects
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  // Inactive projects
  const allInactiveProjects = useAppStore(
    (s) => s.projects.inactiveList,
    shallow
  );
  // Setting the approval Tab Content
  const activeApprovalTab = useAppStore(
    (s) => s.approvalsTabUI.activeApprovalTab,
    shallow
  );
  // Setting the approval Tab Content
  const setActiveApprovalTab = useAppStore(
    (s) => s.approvalsTabUIActions.setActiveApprovalTab,
    shallow
  );

  // Deep link handling via RouteUtils
  const location = useLocation();
  const navigate = useNavigate();
  const delId = getDeepLinkedDelId(location, "approvals");

  // URL normalization handled globally in RouteUtils within DelLandingPage
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  const openApprovalsDelForm = useAppStore(
    (s) => s.approvalsTabUIActions.setShowApprovalsDelForm
  );
  const fetchApprovalDel = useAppStore(
    (s) => s.activeDelApprovalsAwaitingActions.fetchDel
  );
  const approvalsList = useAppStore((s) => s.delLists.approvals.items, shallow);
  const refreshApprovalsList = useAppStore(
    (s) => s.delListsActions.fetchApprovalsPage
  );
  const showApprovalsDelForm = useAppStore(
    (s) => s.approvalsTabUI.showApprovalsDelForm
  );
  const currentApprovalDelId = useAppStore(
    (s) => s.activeDelApprovalsAwaiting?.currentDel?.id
  );
  const selectApprovalRowAction = useAppStore(
    (s) => s.delListsActions.setSelectedApproval
  );
  // Approved helpers for URL-driven view in Approved section
  const approvedList = useAppStore((s) => s.delLists.approved.items, shallow);
  const fetchApprovedDel = useAppStore(
    (s) => s.activeDelApprovedActions.fetchDel
  );
  const openApprovedDelForm = useAppStore(
    (s) => s.approvalsTabUIActions.setShowApprovedDelForm
  );
  const selectApprovedRowAction = useAppStore(
    (s) => s.delListsActions.setSelectedApproved
  );
  const refreshApprovedList = useAppStore(
    (s) => s.delListsActions.fetchApprovedPage
  );

  const processedRef = useRef(null);
  useEffect(() => {
    const run = async () => {
      const parsed = parseHomeRoute(location.pathname, location.search);
      const idStr = parsed.delId;
      const action = parsed.action || "view"; // "approve" | "view"
      const wantsOpen = !!parsed.open;
      const key = `${parsed.tab || ""}:${action || ""}:${idStr || ""}:${
        wantsOpen ? "1" : "0"
      }`;
      if (processedRef.current === key) return;
      processedRef.current = key;

      if (!idStr) {
        // No deep link present; do not force-close an already opened form
        return;
      }

      // No special handling needed for view/approve

      try {
        // Ensure lists are available (best effort)
        if (!Array.isArray(approvalsList) || approvalsList.length === 0) {
          try {
            await refreshApprovalsList(user.employee_id);
          } catch {}
        }
        if (!Array.isArray(approvedList) || approvedList.length === 0) {
          try {
            await refreshApprovedList(user.employee_id);
          } catch {}
        }

        const approvalsItems =
          useAppStore.getState().delLists.approvals.items || [];
        const approvedItems =
          useAppStore.getState().delLists.approved.items || [];
        const inApprovals = approvalsItems.some(
          (row) => String(row.id) === String(idStr)
        );
        const inApproved = approvedItems.some(
          (row) => String(row.id) === String(idStr)
        );

        if (!wantsOpen) {
          // Selection-only; reflect selection and ensure form closed
          if (inApproved) {
            setActiveApprovalTab("approved");
            selectApprovedRowAction({ id: idStr });
            openApprovedDelForm(false);
          } else {
            setActiveApprovalTab("approvals");
            selectApprovalRowAction({ id: idStr });
            openApprovalsDelForm(false);
          }
          return;
        }

        // Close any open form first to prevent rendering stale/null data during fetch
        openApprovalsDelForm(false);
        openApprovedDelForm(false);

        // Open requests
        showLoading("Loading DEL...");
        if (action === "approve") {
          setActiveApprovalTab("approvals");
          const res = await fetchApprovalDel("approve", idStr);
          if (res && res.ok) {
            openApprovalsDelForm(true);
          } else {
            openApprovalsDelForm(false);
            try {
              useAppStore.getState().notificationActions.showNotification({
                type: "error",
                title: "Error Loading DEL",
                message: "DEL not found or could not be loaded.",
              });
            } catch {}
          }
        } else {
          // Default to view. Prefer Approved if id is in approved list
          if (inApproved) {
            setActiveApprovalTab("approved");
            const res = await fetchApprovedDel("view", idStr);
            if (res && res.ok) {
              openApprovedDelForm(true);
            } else {
              openApprovedDelForm(false);
              try {
                useAppStore.getState().notificationActions.showNotification({
                  type: "error",
                  title: "Error Loading DEL",
                  message: "DEL not found or could not be loaded.",
                });
              } catch {}
            }
          } else {
            setActiveApprovalTab("approvals");
            const res = await fetchApprovalDel("view", idStr);
            if (res && res.ok) {
              openApprovalsDelForm(true);
            } else {
              openApprovalsDelForm(false);
              try {
                useAppStore.getState().notificationActions.showNotification({
                  type: "error",
                  title: "Error Loading DEL",
                  message: "DEL not found or could not be loaded.",
                });
              } catch {}
            }
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to process deep link", err);
      } finally {
        hideLoading();
      }
    };
    run();
  }, [
    location.pathname,
    location.search,
    user?.employee_id,
    approvalsList?.length,
    setActiveApprovalTab,
    showLoading,
    hideLoading,
    fetchApprovalDel,
    openApprovalsDelForm,
    selectApprovalRowAction,
    navigate,
  ]);

  // Back/forward: reflect URL -> UI (selection/form state)
  useEffect(() => {
    const {
      tab,
      delId: urlDelId,
      open,
    } = parseHomeRoute(location.pathname, location.search);
    if (tab !== "approvals") return;
    if (!urlDelId) {
      if (showApprovalsDelForm) openApprovalsDelForm(false);
      return;
    }
    if (!open) {
      selectApprovalRowAction({ id: urlDelId });
      if (showApprovalsDelForm) openApprovalsDelForm(false);
    }
  }, [
    location.pathname,
    location.search,
    showApprovalsDelForm,
    openApprovalsDelForm,
    selectApprovalRowAction,
  ]);

  return (
    <div className={styles.approvalsTabContainer}>
      <ApprovalTabButtons
        activeApprovalTab={activeApprovalTab}
        setActiveApprovalTab={setActiveApprovalTab}
      />
      <div className={styles.approvalsTabContent}>
        {activeApprovalTab === "approvals" && (
          <ApprovalsTabContent
            user={user}
            employees={employees}
            allEmployees={allSearchEmployees}
            allProjectInfo={allProjectInfo}
            allInactiveProjects={allInactiveProjects}
          />
        )}
        {activeApprovalTab === "approved" && (
          <ApprovedTabContent
            user={user}
            employees={employees}
            allEmployees={allSearchEmployees}
            allProjectInfo={allProjectInfo}
            allInactiveProjects={allInactiveProjects}
          />
        )}
      </div>
    </div>
  );
};
